import { tw, twMerge } from '@panwds/tailwindcss';
import React, { forwardRef, useRef, useState } from 'react';
import { mergeRefs, noop } from '../../utils/index.js';
import { DangerIcon, SuccessIcon } from '../Icon/index.js';
import { horizontalPadding, selectBorderColor } from './input.logic.js';
import { CopyButton } from '../Button/index.js';
import { ClearButton } from './ClearButton.js';
const sizes = {
    sm: tw `tw-text-xs`,
    md: tw `tw-text-xs`,
    lg: tw `tw-text-sm`
};
const height = {
    sm: tw `tw-h-6`,
    md: tw `tw-h-8`,
    lg: tw `tw-h-10`
};
const defaultProps = {
    allowCopy: false,
    danger: false,
    disabled: false,
    readOnly: false,
    success: false,
    type: 'text'
};
/**
 * Lets users enter and edit text.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { Input } from "@panwds/react-ui";
 * ```
 */
export const Input = forwardRef(({ addClassName, allowClear, allowCopy, appearance, dataMetrics, dataTestId, danger, disabled, onKeyDown, onChange, placeholder, prefix, readOnly, size = 'md', success, suffix, value, type, onFocus = noop, onBlur = noop, onMouseEnter = noop, onMouseLeave = noop, ...passThrough }, userRef) => {
    const isControlled = useRef(value !== undefined);
    const internalRef = useRef();
    const [isActive, setIsActive] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const ref = mergeRefs([userRef, internalRef]);
    const hasError = danger || appearance === 'error';
    const isSuccess = success || appearance === 'success';
    const rootClassName = twMerge('tw-flex tw-items-center tw-border-solid tw-border tw-rounded tw-box-border tw-gap-2', selectBorderColor({ hasError, active: isActive, hover: isHovered, readOnly, disabled }), readOnly
        ? 'tw-bg-black dark:tw-bg-white tw-bg-opacity-[0.04] dark:tw-bg-opacity-[0.04]'
        : 'tw-bg-white dark:tw-bg-black tw-bg-opacity-80 dark:tw-bg-opacity-16', disabled && 'tw-text-disabled tw-cursor-not-allowed', height[size], horizontalPadding(size, !!prefix, !!suffix), (isSuccess || hasError || allowCopy || allowClear) && !suffix && 'tw-pr-2', addClassName);
    const inputClassName = twMerge('tw-font-lato tw-border-0 focus:tw-outline-none tw-placeholder-gray-500 dark:tw-placeholder-gray-400 dark:tw-text-gray-100 tw-flex-auto tw-bg-transparent tw-p-0 tw-min-w-16 tw-max-w-full tw-w-full', disabled ? 'tw-cursor-not-allowed tw-text-disabled dark:tw-text-gray-600' : 'tw-text-gray-900', readOnly && 'dark:tw-text-gray-100', sizes[size]);
    const handleFocus = (e) => {
        onFocus(e);
        setIsActive(true);
    };
    const handleBlur = (e) => {
        onBlur(e);
        setIsActive(false);
    };
    const handleMouseEnter = (e) => {
        onMouseEnter(e);
        setIsHovered(true);
    };
    const handleMouseLeave = (e) => {
        onMouseLeave(e);
        setIsHovered(false);
    };
    return (React.createElement("span", { className: rootClassName },
        prefix,
        React.createElement("input", { ref: ref, className: inputClassName, "data-metrics": dataMetrics, "data-testid": dataTestId || 'input', disabled: disabled, onChange: onChange, onKeyDown: onKeyDown, onFocus: handleFocus, onBlur: handleBlur, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, placeholder: placeholder, value: value, readOnly: readOnly, type: type, ...passThrough }),
        allowClear && (React.createElement(ClearButton, { addClassName: "tw-p-0.5", style: { marginRight: '-0.125rem' }, iconSize: size, onClick: (e) => {
                if (isControlled.current) {
                    if (internalRef.current && onChange) {
                        onChange(makeMockClearEvent(internalRef.current, e));
                    }
                }
                else {
                    internalRef.current?.setRangeText('', 0, Number.MAX_SAFE_INTEGER);
                }
            } })),
        allowCopy && (React.createElement(CopyButton, { iconSize: size, addClassName: "tw-p-0.5", style: { marginRight: '-0.125rem', width: '1.5rem', height: '1.5rem' }, text: () => internalRef.current?.value })),
        isSuccess && React.createElement(SuccessIcon, { size: "lg" }),
        hasError && React.createElement(DangerIcon, { size: "lg" }),
        suffix));
});
function makeMockClearEvent(inputRef, clickEvent) {
    const updatedTarget = { ...inputRef, value: '' };
    const { nativeEvent, bubbles, cancelable, defaultPrevented, eventPhase, isTrusted, preventDefault, isDefaultPrevented, stopPropagation, isPropagationStopped, persist, timeStamp } = clickEvent;
    return {
        currentTarget: updatedTarget,
        target: updatedTarget,
        nativeEvent,
        bubbles,
        cancelable,
        defaultPrevented,
        eventPhase,
        isTrusted,
        preventDefault,
        isDefaultPrevented,
        stopPropagation,
        isPropagationStopped,
        persist,
        timeStamp,
        type: 'clear'
    };
}
Input.displayName = 'Input';
Input.defaultProps = defaultProps;
//# sourceMappingURL=Input.js.map